'use es6';

import DefaultIllustrationNames from 'feedback-schema/constants/illustrations/thankYou';
import * as RespondentTypes from 'feedback-schema/constants/respondentTypes';
import * as SurveyTypes from 'feedback-schema/constants/surveyTypes';
import { oneOfObjectKeys } from 'feedback-utils/propTypes';
import { makeSafeInnerHTML } from 'feedback-utils/sanitize';
import { Record } from 'immutable';
import EmailAddressRegex from 'PatternValidationJS/patterns/EmailAddress';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { NavMarker } from 'react-rhumb';
import UIIllustration from 'UIComponents/image/UIIllustration';
import UIImage from 'UIComponents/image/UIImage';
import UITextInput from 'UIComponents/input/UITextInput';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UIButton from 'UIComponents/button/UIButton';
import UILink from 'UIComponents/link/UILink';
import { LegalConsentOptions, Selectors } from '../../Constants';
import ImagePage from '../ImagePage';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class ThankYou extends Component {
  constructor(props) {
    super(props);
    this.renderSubmitButton = this.renderSubmitButton.bind(this);
    this.state = {
      communicationConsentIsChecked: false,
      email: null,
      explicitProcessingConsentIsChecked: false
    };
  }
  isSubmitEnabled() {
    const {
      props: {
        config: {
          gdprSettings
        }
      }
    } = this;
    const gdprConsentType = gdprSettings && gdprSettings.gdprConsentType;
    const {
      email,
      explicitProcessingConsentIsChecked
    } = this.state;
    if (!email || !EmailAddressRegex.test(email)) {
      return false;
    }
    return !gdprConsentType || gdprConsentType !== LegalConsentOptions.SEPARATE_CONSENT || explicitProcessingConsentIsChecked;
  }
  maybeRenderGDPRDetails(shouldShowGDPRConsent, gdprSettings, submitButtonLabel) {
    if (!shouldShowGDPRConsent) {
      return null;
    }
    const {
      communicationConsentCheckboxLabel,
      communicationConsentText,
      explicitProcessingConsentCheckboxLabel,
      explicitProcessingConsentText,
      gdprConsentType,
      implicitProcessingConsentText,
      legitimateInterestPrivacyPolicyText,
      privacyPolicyText
    } = gdprSettings;
    return /*#__PURE__*/_jsxs(View, {
      marginBlockStart: "500",
      paddingBlock: "400",
      paddingInline: "200",
      inlineSize: "100%",
      children: [(gdprConsentType === LegalConsentOptions.IMPLICIT_CONSENT || gdprConsentType === LegalConsentOptions.SEPARATE_CONSENT) && /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(Text, {
          as: "small",
          marginBlockStart: "400",
          dangerouslySetHTML: makeSafeInnerHTML(communicationConsentText)
        }), /*#__PURE__*/_jsx(UICheckbox, {
          className: "m-y-2",
          checked: this.state.communicationConsentIsChecked,
          onChange: ({
            target: {
              checked
            }
          }) => {
            this.setState({
              communicationConsentIsChecked: checked
            });
          },
          size: "small",
          children: /*#__PURE__*/_jsx("span", {
            dangerouslySetInnerHTML: makeSafeInnerHTML(communicationConsentCheckboxLabel)
          })
        })]
      }), gdprConsentType === LegalConsentOptions.SEPARATE_CONSENT && /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(Text, {
          as: "small",
          marginBlockStart: "400",
          dangerouslySetHTML: makeSafeInnerHTML(explicitProcessingConsentText)
        }), /*#__PURE__*/_jsx(UICheckbox, {
          className: "m-y-2",
          checked: this.state.explicitProcessingConsentIsChecked,
          onChange: ({
            target: {
              checked
            }
          }) => {
            this.setState({
              explicitProcessingConsentIsChecked: checked
            });
          },
          size: "small",
          children: /*#__PURE__*/_jsx("span", {
            dangerouslySetInnerHTML: makeSafeInnerHTML(explicitProcessingConsentCheckboxLabel)
          })
        })]
      }), (gdprConsentType === LegalConsentOptions.IMPLICIT_CONSENT || gdprConsentType === LegalConsentOptions.SEPARATE_CONSENT) && /*#__PURE__*/_jsx(Text, {
        as: "small",
        marginBlockStart: "400",
        dangerouslySetHTML: makeSafeInnerHTML(privacyPolicyText)
      }), gdprConsentType === LegalConsentOptions.IMPLICIT_CONSENT && /*#__PURE__*/_jsx(Text, {
        as: "small",
        marginBlockStart: "400",
        dangerouslySetInnerHTML: makeSafeInnerHTML(implicitProcessingConsentText)
      }), gdprConsentType === LegalConsentOptions.LEGITIMATE_INTEREST && /*#__PURE__*/_jsx(Text, {
        as: "small",
        marginBlockStart: "400",
        dangerouslySetInnerHTML: makeSafeInnerHTML(legitimateInterestPrivacyPolicyText)
      }), gdprConsentType !== LegalConsentOptions.NONE && this.renderSubmitButton(submitButtonLabel)]
    });
  }
  renderSubmitButton(submitButtonLabel) {
    const {
      communicationConsentIsChecked,
      email
    } = this.state;
    return /*#__PURE__*/_jsx(View, {
      marginBlockStart: "200",
      display: "block",
      children: /*#__PURE__*/_jsx(UIButton, {
        "data-selenium-test": Selectors.SUBMIT_BUTTON,
        disabled: !this.isSubmitEnabled(),
        onClick: () => this.props.onSubmitClick(email, communicationConsentIsChecked),
        use: "primary",
        children: submitButtonLabel
      })
    });
  }
  render() {
    const {
      onClose,
      onImageLoad,
      respondentType,
      webRespondent: {
        hasEmail
      },
      config: {
        closeLabel,
        gdprSettings,
        surveyType,
        thankYou: {
          bodies: {
            [respondentType.toLowerCase()]: body
          },
          headers: {
            [respondentType.toLowerCase()]: heading
          },
          images: {
            [respondentType.toLowerCase()]: imageConfig
          }
        },
        submitLabel: submitButtonLabel
      }
    } = this.props;
    const shouldShowGDPRConsent = !hasEmail && surveyType === SurveyTypes.CSAT && gdprSettings && gdprSettings.gdprConsentType !== LegalConsentOptions.NONE && gdprSettings.privacyPolicyText;
    return /*#__PURE__*/_jsx(ImagePage, {
      content: this.maybeRenderGDPRDetails(shouldShowGDPRConsent, gdprSettings, submitButtonLabel),
      image: imageConfig.hasImage && (imageConfig.imageUrl ? /*#__PURE__*/_jsx(UIImage, {
        src: imageConfig.imageUrl,
        onLoad: onImageLoad,
        alt: "thank you image"
      }) : /*#__PURE__*/_jsx(UIIllustration, {
        onLoad: onImageLoad,
        alt: DefaultIllustrationNames[respondentType],
        name: DefaultIllustrationNames[respondentType]
      })),
      children: /*#__PURE__*/_jsxs("div", {
        className: "m-y-4",
        "data-selenium-test": Selectors.THANK_YOU.CONTAINER,
        children: [/*#__PURE__*/_jsx("div", {
          className: "heading thank-you-content",
          "data-selenium-test": Selectors.THANK_YOU.HEADER,
          children: /*#__PURE__*/_jsx(NavMarker, {
            name: "thank_you",
            children: heading
          })
        }), /*#__PURE__*/_jsx("p", {
          className: "thank-you-content",
          "data-selenium-test": Selectors.THANK_YOU.BODY,
          dangerouslySetInnerHTML: makeSafeInnerHTML(body)
        }), surveyType === SurveyTypes.CSAT && !hasEmail && gdprSettings && /*#__PURE__*/_jsx(UITextInput, {
          className: "m-bottom-3",
          "data-selenium-test": Selectors.EMAIL_INPUT,
          onChange: ({
            target: {
              value
            }
          }) => this.setState({
            email: value
          }),
          placeholder: "name@email.com"
        }), (surveyType === SurveyTypes.NPS || hasEmail || !gdprSettings) && /*#__PURE__*/_jsx(UILink, {
          onClick: onClose,
          "data-selenium-test": Selectors.CLOSE_BUTTON,
          children: closeLabel
        }), surveyType === SurveyTypes.CSAT && !hasEmail && gdprSettings && !shouldShowGDPRConsent && this.renderSubmitButton(submitButtonLabel)]
      })
    });
  }
}
ThankYou.propTypes = {
  config: PropTypes.instanceOf(Record).isRequired,
  onClose: PropTypes.func.isRequired,
  onImageLoad: PropTypes.func.isRequired,
  onSubmitClick: PropTypes.func.isRequired,
  respondentType: oneOfObjectKeys(RespondentTypes),
  webRespondent: PropTypes.instanceOf(Record)
};
export default ThankYou;